(function (root) {
  'use strict';

  function calculate(rules, context, cart) {
    if (!Array.isArray(rules) || !context || !cart || !/^[A-Z]{2}$/.test(context.country || '') ||
        !/^[A-Z]{3}$/.test(context.currency || '') || cart.currency !== context.currency ||
        !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 ||
        !Number.isSafeInteger(cart.item_count) || cart.item_count < 1) return null;
    const matches = rules.filter((rule) => rule?.country === context.country && rule?.currency === context.currency);
    if (matches.length !== 1) return null;
    const rule = matches[0];
    if (rule.region_label != null && (typeof rule.region_label !== 'string' || !rule.region_label.trim() || rule.region_label.length > 80)) return null;
    if (rule.basis !== 'cart_total_after_discounts' || rule.scope !== 'all_items' ||
        !Number.isSafeInteger(rule.standard) || rule.standard < 0 ||
        (rule.express != null && (!Number.isSafeInteger(rule.express) || rule.express < rule.standard))) return null;
    return {
      country: rule.country, currency: rule.currency,
      ...(rule.region_label ? { regionLabel: rule.region_label.trim() } : {}),
      standard: Math.max(0, rule.standard - cart.total_price),
      express: rule.express == null ? null : Math.max(0, rule.express - cart.total_price)
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { calculate };
  if (!root.customElements || root.customElements.get('alvaura-cart-thresholds')) return;

  class AlvauraCartThresholds extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      try { this.rules = JSON.parse(this.dataset.rules); } catch (_) { return; }
      this.context = { country: this.dataset.country, currency: this.dataset.currency };
      this.render({ total_price: Number(this.dataset.total), item_count: Number(this.dataset.count), currency: this.context.currency });
      this.onUpdate = (event) => {
        this.version = (this.version || 0) + 1;
        clearTimeout(this.timer);
        const data = event?.cartData || event?.detail?.data || event?.detail;
        if (data && Number.isSafeInteger(data.total_price) && typeof data.currency === 'string') this.render(data);
        else this.scheduleRefresh();
      };
      ['cart:updated', 'DiscountUpdateEvent', 'GiftWrapUpdateEvent', 'bundler:total_cart_values'].forEach((name) => document.addEventListener(name, this.onUpdate));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onUpdate);
      }
    }

    disconnectedCallback() {
      this.version = (this.version || 0) + 1;
      ['cart:updated', 'DiscountUpdateEvent', 'GiftWrapUpdateEvent', 'bundler:total_cart_values'].forEach((name) => document.removeEventListener(name, this.onUpdate));
      this.unsubscribe?.();
      clearTimeout(this.timer);
    }

    scheduleRefresh() {
      this.version = (this.version || 0) + 1;
      this.hidden = true; // Never leave an old threshold visible during a discount/currency refresh.
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 0);
    }

    async refresh() {
      const version = (this.version || 0) + 1;
      this.version = version;
      try {
        const response = await fetch(this.dataset.cartUrl, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart unavailable');
        const cart = await response.json();
        if (this.isConnected && this.version === version) this.render(cart);
      } catch (_) { if (this.isConnected && this.version === version) this.hidden = true; }
    }

    render(cart) {
      const result = calculate(this.rules, this.context, cart);
      this.hidden = !result;
      if (!result) return;
      let format;
      try { format = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: result.currency }); }
      catch (_) { this.hidden = true; return; }
      this.querySelector('[data-shipping-context]').textContent = `${result.regionLabel || result.country} · ${result.currency}`;
      const rule = this.rules.find((entry) => entry.country === result.country && entry.currency === result.currency);
      const progress = this.querySelector('[data-shipping-progress]');
      if (progress) {
        progress.value = rule.standard === 0 ? 100 : Math.min(100, Math.max(0, (rule.standard - result.standard) / rule.standard * 100));
      }
      for (const tier of ['standard', 'express']) {
        const line = this.querySelector(`[data-shipping-${tier}]`);
        line.hidden = result[tier] == null;
        if (result[tier] == null) { line.textContent = ''; continue; }
        const label = tier === 'standard' && rule.standard === 0
          ? this.dataset.standardNoMinimum
          : this.dataset[`${tier}${result[tier] === 0 ? 'Met' : 'Remaining'}`];
        if (!label) { this.hidden = true; return; }
        line.textContent = label.replace('__AMOUNT__', format.format(result[tier] / 100));
      }
      if (progress) progress.setAttribute('aria-label', `${this.querySelector('[data-shipping-standard]').textContent} · ${result.regionLabel || result.country} · ${result.currency}`);
    }
  }
  root.customElements.define('alvaura-cart-thresholds', AlvauraCartThresholds);
})(typeof window === 'undefined' ? globalThis : window);
